import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Container from '../../components/Container';
import ComponentErrorBoundary from '../../components/ComponentErrorBoundary';
import AssetUploadForm from '../../components/AssetUploadForm';
import SEO from '../../lib/seo';
import { assetsApi, projectsApi, clientsApi, formatDate, formatFileSize } from '../../lib/adminApi';

/**
 * Admin Assets Management Page
 * @returns {React.ReactElement} Assets management interface
 */
const AdminAssets = () => {
  const [searchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProjectId, setUploadProjectId] = useState('');
  const [editingAsset, setEditingAsset] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [downloadingAsset, setDownloadingAsset] = useState(null);
  const [filterProjectId, setFilterProjectId] = useState(searchParams.get('project') || '');
  const filterClientId = searchParams.get('client') || '';
  const [filterKind, setFilterKind] = useState('');

  useEffect(() => {
    loadData();
  }, [filterProjectId, filterClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [assetsData, projectsData, clientsData] = await Promise.all([
        filterProjectId ? assetsApi.getByProjectId(filterProjectId) : assetsApi.getAll(),
        projectsApi.getAll(),
        clientsApi.getAll()
      ]);
      
      // Filter assets by client if client filter is active
      let filteredAssets = assetsData;
      if (filterClientId && !filterProjectId) {
        // Filter assets by client through projects
        const clientProjects = projectsData.filter(project => project.client_id === filterClientId);
        const clientProjectIds = clientProjects.map(project => project.id);
        filteredAssets = assetsData.filter(asset => clientProjectIds.includes(asset.project_id));
      }
      
      setAssets(filteredAssets);
      setProjects(projectsData);
      setClients(clientsData);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load assets:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleUpdateAsset = async (assetData) => {
    try {
      await assetsApi.update(editingAsset.id, assetData);
      await loadData();
      setEditingAsset(null);
    } catch (err) {
      alert(`Failed to update asset: ${err.message}`);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset? This action cannot be undone.')) {
      return;
    }

    try {
      await assetsApi.delete(assetId);
      await loadData();
      if (selectedAsset?.id === assetId) {
        setSelectedAsset(null);
      }
    } catch (err) {
      alert(`Failed to delete asset: ${err.message}`);
    }
  };

  const handleUploadComplete = async (assetData) => {
    try {
      // Reload data to show the new asset
      await loadData();
      setShowUploadModal(false);
      setUploadProjectId('');
      
      // Show success message
      alert(`File uploaded successfully: ${assetData.filename}`);
    } catch (err) {
      console.error('Error after upload:', err);
    }
  };

  const handleUploadCancel = () => {
    setShowUploadModal(false);
    setUploadProjectId('');
  };

  const handleDownload = async (asset) => {
    try {
      setDownloadingAsset(asset.id);
      
      // Get download URL from the API
      const data = await assetsApi.getDownloadLink(asset.id);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = data.download_url;
      link.download = asset.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (err) {
      alert(`Failed to download file: ${err.message}`);
    } finally {
      setDownloadingAsset(null);
    }
  };

  const startUpload = (projectId = '') => {
    setUploadProjectId(projectId);
    setShowUploadModal(true);
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.title : projectId;
  };

  const getClientName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return 'Unknown';
    const client = clients.find(c => c.id === project.client_id);
    return client ? client.display_name : project.client_id;
  };

  const getClientNameById = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.display_name : clientId;
  };

  const getKindIcon = (kind) => {
    switch (kind) {
      case 'video':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'photo':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case 'zip':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'doc':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  const filteredAssets = assets.filter(asset => {
    if (filterKind && asset.kind !== filterKind) return false;
    return true;
  });

  const AssetForm = ({ asset, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      id: asset?.id || '',
      project_id: asset?.project_id || filterProjectId || '',
      r2_key: asset?.r2_key || '',
      filename: asset?.filename || '',
      description: asset?.description || '',
      kind: asset?.kind || 'other',
      size_bytes: asset?.size_bytes || '',
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit({
        ...formData,
        size_bytes: formData.size_bytes ? parseInt(formData.size_bytes) : null,
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">
            {asset ? 'Edit Asset' : 'Create New Asset'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Asset ID
              </label>
              <input
                type="text"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="e.g., asset-001"
                required
                disabled={!!asset} // Can't change ID when editing
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project
              </label>
              <select
                value={formData.project_id}
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Select a project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title} ({getClientName(project.id)})
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                R2 Key (Storage Path)
              </label>
              <input
                type="text"
                value={formData.r2_key}
                onChange={(e) => setFormData({ ...formData, r2_key: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="e.g., clients/acme-realty/proj-001/video.mp4"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filename
              </label>
              <input
                type="text"
                value={formData.filename}
                onChange={(e) => setFormData({ ...formData, filename: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="e.g., Aerial View.mp4"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-gray-500">(visible to client)</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-blue-500 focus:border-brand-blue-500 resize-none"
                placeholder="Describe this asset for the client (e.g., 'Aerial photos of the north wing', 'Final edited video with music')"
                rows="3"
                maxLength="1000"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.description.length}/1000 characters
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Asset Type
              </label>
              <select
                value={formData.kind}
                onChange={(e) => setFormData({ ...formData, kind: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              >
                <option value="video">Video</option>
                <option value="photo">Photo</option>
                <option value="zip">ZIP Archive</option>
                <option value="doc">Document</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File Size (bytes)
              </label>
              <input
                type="number"
                value={formData.size_bytes}
                onChange={(e) => setFormData({ ...formData, size_bytes: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="e.g., 15728640"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-brand-blue-600 text-white rounded-md hover:bg-brand-blue-700"
              >
                {asset ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-blue-600">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue-600 mx-auto mb-4"></div>
            <p className="text-white">Loading assets...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-blue-600">
      <SEO 
        title={
          filterClientId ? `Manage Assets - ${getClientNameById(filterClientId)} | LSAS Admin Portal` :
          filterProjectId ? `Manage Assets - ${getProjectName(filterProjectId)} | LSAS Admin Portal` :
          "Manage Assets | LSAS Admin Portal"
        }
        description={
          filterClientId ? "Upload and organize files for this client" :
          filterProjectId ? "Upload and organize files for this project" :
          "Upload and organize client deliverable files"
        }
        url={`/admin/assets${filterClientId ? `?client=${filterClientId}` : filterProjectId ? `?project=${filterProjectId}` : ''}`}
      />
      
      <Header />
      
      {/* Admin Header */}
      <section className="pt-40 pb-8 bg-brand-black text-white">
        <Container>
          <div className="flex items-center justify-between">
            <div>
              <nav className="text-sm mb-2">
                <Link to="/admin" className="text-gray-400 hover:text-white">Admin</Link>
                <span className="text-gray-400 mx-2">/</span>
                {filterClientId && (
                  <>
                    <Link to="/admin/clients" className="text-gray-400 hover:text-white">Clients</Link>
                    <span className="text-gray-400 mx-2">/</span>
                  </>
                )}
                {filterProjectId && !filterClientId && (
                  <>
                    <Link to="/admin/projects" className="text-gray-400 hover:text-white">Projects</Link>
                    <span className="text-gray-400 mx-2">/</span>
                  </>
                )}
                <span className="text-white">Assets</span>
              </nav>
              <h1 className="text-3xl font-bold mb-2">
                {filterClientId ? `Manage Assets - ${getClientNameById(filterClientId)}` :
                 filterProjectId ? `Manage Assets - ${getProjectName(filterProjectId)}` :
                 'Manage Assets'}
              </h1>
              <p className="text-gray-300">
                {filterClientId ? 'Upload and organize files for this client' :
                 filterProjectId ? 'Upload and organize files for this project' :
                 'Upload and organize client deliverable files'}
              </p>
            </div>
            <div className="flex space-x-3">
              <Link
                to="/admin"
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                ← Back to Dashboard
              </Link>
              {filterClientId && (
                <Link
                  to={`/admin/clients`}
                  className="px-4 py-2 bg-brand-blue-600 text-white rounded-md hover:bg-brand-blue-700"
                >
                  ← Back to Clients
                </Link>
              )}
              {filterProjectId && !filterClientId && (
                <Link
                  to={`/admin/projects`}
                  className="px-4 py-2 bg-brand-blue-600 text-white rounded-md hover:bg-brand-blue-700"
                >
                  ← Back to Projects
                </Link>
              )}
              {(filterClientId || filterProjectId) && (
                <button
                  onClick={() => startUpload(filterProjectId)}
                  className="px-4 py-2 bg-brand-green-600 text-white rounded-md hover:bg-brand-green-700"
                >
                  Upload File
                </button>
              )}
            </div>
          </div>
        </Container>
      </section>

      <main className="flex-grow py-8">
        <Container>
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <p className="font-semibold">Error loading assets:</p>
              <p>{error}</p>
              <button 
                onClick={loadData}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* Filters */}
          <div className="mb-6 bg-white rounded-xl shadow-card p-4">
            <div className="flex items-center space-x-4 flex-wrap gap-2">
              <label className="text-sm font-medium text-gray-700">Filter by Project:</label>
              <select
                value={filterProjectId}
                onChange={(e) => setFilterProjectId(e.target.value)}
                className="p-2 border border-gray-300 rounded-md"
              >
                <option value="">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title} ({getClientName(project.id)})
                  </option>
                ))}
              </select>
              
              <label className="text-sm font-medium text-gray-700">Filter by Type:</label>
              <select
                value={filterKind}
                onChange={(e) => setFilterKind(e.target.value)}
                className="p-2 border border-gray-300 rounded-md"
              >
                <option value="">All Types</option>
                <option value="video">Video</option>
                <option value="photo">Photo</option>
                <option value="zip">ZIP</option>
                <option value="doc">Document</option>
                <option value="other">Other</option>
              </select>
              
              {(filterProjectId || filterKind) && (
                <button
                  onClick={() => {
                    setFilterProjectId('');
                    setFilterKind('');
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Assets List */}
            <div className="lg:col-span-2">
              <ComponentErrorBoundary 
                fallbackMessage="Assets list temporarily unavailable"
                componentName="AssetsList"
              >
                <div className="bg-white rounded-xl shadow-card">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold">
                      Assets ({filteredAssets.length})
                      {filterProjectId && (
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          for {getProjectName(filterProjectId)}
                        </span>
                      )}
                    </h2>
                  </div>
                  
                  {filteredAssets.length > 0 ? (
                    <div className="divide-y divide-gray-200">
                      {filteredAssets.map((asset) => (
                        <div key={asset.id} className="p-6 hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <div className={`p-2 rounded-lg ${
                                asset.kind === 'video' ? 'bg-purple-100 text-purple-600' :
                                asset.kind === 'photo' ? 'bg-green-100 text-green-600' :
                                asset.kind === 'zip' ? 'bg-blue-100 text-blue-600' :
                                asset.kind === 'doc' ? 'bg-yellow-100 text-yellow-600' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {getKindIcon(asset.kind)}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-medium text-gray-900 truncate">
                                  {asset.filename}
                                </h3>
                                {asset.description && (
                                  <p className="text-sm text-gray-700 mt-1 italic">
                                    "{asset.description}"
                                  </p>
                                )}
                                <p className="text-sm text-gray-600">
                                  Project: {getProjectName(asset.project_id)}
                                </p>
                                <p className="text-sm text-gray-500">
                                  Client: {getClientName(asset.project_id)}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                  ID: {asset.id}
                                </p>
                                <div className="flex items-center space-x-4 mt-2">
                                  <span className={`px-2 py-1 text-xs rounded ${
                                    asset.kind === 'video' ? 'bg-purple-100 text-purple-800' :
                                    asset.kind === 'photo' ? 'bg-green-100 text-green-800' :
                                    asset.kind === 'zip' ? 'bg-blue-100 text-blue-800' :
                                    asset.kind === 'doc' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {asset.kind}
                                  </span>
                                  {asset.size_bytes && (
                                    <span className="text-xs text-gray-500">
                                      {formatFileSize(asset.size_bytes)}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400 mt-2">
                                  Created: {formatDate(asset.created_at)}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex space-x-2 ml-4">
                              <button
                                onClick={() => setSelectedAsset(asset)}
                                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleDownload(asset)}
                                disabled={downloadingAsset === asset.id}
                                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {downloadingAsset === asset.id ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-700 mr-1 inline-block"></div>
                                    Downloading...
                                  </>
                                ) : (
                                  'Download'
                                )}
                              </button>
                              <button
                                onClick={() => setEditingAsset(asset)}
                                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteAsset(asset.id)}
                                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <p>No assets found</p>
                    </div>
                  )}
                </div>
              </ComponentErrorBoundary>
            </div>

            {/* Asset Details */}
            <div>
              <ComponentErrorBoundary 
                fallbackMessage="Asset details temporarily unavailable"
                componentName="AssetDetails"
              >
                <div className="bg-white rounded-xl shadow-card">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold">Asset Details</h2>
                  </div>
                  
                  {selectedAsset ? (
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        <div className={`p-3 rounded-lg mr-4 ${
                          selectedAsset.kind === 'video' ? 'bg-purple-100 text-purple-600' :
                          selectedAsset.kind === 'photo' ? 'bg-green-100 text-green-600' :
                          selectedAsset.kind === 'zip' ? 'bg-blue-100 text-blue-600' :
                          selectedAsset.kind === 'doc' ? 'bg-yellow-100 text-yellow-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {getKindIcon(selectedAsset.kind)}
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {selectedAsset.filename}
                          </h3>
                          <span className={`px-2 py-1 text-xs rounded ${
                            selectedAsset.kind === 'video' ? 'bg-purple-100 text-purple-800' :
                            selectedAsset.kind === 'photo' ? 'bg-green-100 text-green-800' :
                            selectedAsset.kind === 'zip' ? 'bg-blue-100 text-blue-800' :
                            selectedAsset.kind === 'doc' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedAsset.kind}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-6">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Asset ID</p>
                          <p className="text-sm text-gray-600">{selectedAsset.id}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Project</p>
                          <p className="text-sm text-gray-600">{getProjectName(selectedAsset.project_id)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Client</p>
                          <p className="text-sm text-gray-600">{getClientName(selectedAsset.project_id)}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Storage Path</p>
                          <p className="text-sm text-gray-600 break-all">{selectedAsset.r2_key}</p>
                        </div>
                        {selectedAsset.size_bytes && (
                          <div>
                            <p className="text-sm font-medium text-gray-700">File Size</p>
                            <p className="text-sm text-gray-600">{formatFileSize(selectedAsset.size_bytes)}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-700">Created</p>
                          <p className="text-sm text-gray-600">{formatDate(selectedAsset.created_at)}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <Link
                          to={`/admin/projects?highlight=${selectedAsset.project_id}`}
                          className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded text-center hover:bg-blue-200"
                        >
                          View Project
                        </Link>
                        <button
                          onClick={() => handleDownload(selectedAsset)}
                          disabled={downloadingAsset === selectedAsset.id}
                          className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloadingAsset === selectedAsset.id ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-700 mr-1 inline-block"></div>
                              Downloading...
                            </>
                          ) : (
                            'Download'
                          )}
                        </button>
                      </div>
                      <button
                        onClick={() => setEditingAsset(selectedAsset)}
                        className="w-full px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        Edit Asset
                      </button>
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>Select an asset to view details</p>
                    </div>
                  )}
                </div>
              </ComponentErrorBoundary>
            </div>
          </div>
        </Container>
      </main>

      {/* Modals */}

      {editingAsset && (
        <AssetForm
          asset={editingAsset}
          onSubmit={handleUpdateAsset}
          onCancel={() => setEditingAsset(null)}
        />
      )}

      {showUploadModal && (
        <AssetUploadForm
          projectId={uploadProjectId}
          projects={filterClientId ? projects.filter(p => p.client_id === filterClientId) : projects}
          onUploadComplete={handleUploadComplete}
          onCancel={handleUploadCancel}
        />
      )}

      <Footer />
    </div>
  );
};

export default AdminAssets;
